import moment, { unitOfTime } from 'moment-timezone';

import {
  ActionSearchInput,
  ApiActionsCreators,
  ApiAssignees,
} from '../../services/v1/actions';

import {
  SourceType as ActionComposeSourceType,
  ActionType as ActionComposeActionType,
} from './actionCompose';
import getClient from './getClient';
// TODO: Check these (PLAYB-155 / WFA-110)

export type GetActionsResponse = {
  data: Action[];
};

export type ActionPriorityTypes = 'high' | 'normal' | 'low' | 'none';

export type EngagementScoreType = Exclude<ActionPriorityTypes, 'none'>;

// Double check with omnibar action type for overlap
// libs/api/src/lib/services/v1/actions.ts
export type Action = {
  id: number;
  summary: string;
  source: SourceType;
  who: Who;
  due_at: string;
  creator: Person;
  assignee: Person;
  body?: string;
  subject?: string;
  priority?: ActionPriorityTypes;
  deleted_at?: string;
  completed_at?: string;
  failed?: boolean;
  type?: ActionType;
  smart_priority?: number;
  summary_v2?: string[];
  step_id?: number;
  flow_id?: number;
  prepared_email_body?: string;
  prepared_email_subject?: string;
  block_message?: string;
  is_outbox_action?: string;
  execute_at?: string;
  failed_reason?: string;
};

export type SourceType = ActionComposeSourceType;
export type ActionType = ActionComposeActionType | AdditionalActionTypes;
type AdditionalActionTypes =
  // overlap:
  //   | 'CALL'
  //   | 'GENERAL'
  //   | 'OTHER'
  | 'EMAIL'
  | 'IN_MAIL'
  | 'LINKEDIN_CONNECT'
  | 'SENDOSO'
  | 'SMS'
  // Are these still a thing?
  | 'IN_PERSON_VISIT'
  | 'DIRECT_MAIL'
  | 'WORKSPACE'
  | 'SALESFORCE';

type PersonRollup = {
  created_at: string;
  effort: number;
  email_link_clicks: number;
  email_opens: number;
  email_replies: number;
  emails_sent_via_connect: number;
  emails_sent_via_flow_send: number;
  emails_sent_via_flow_send_all: number;
  email_unsubscribe_replies: number;
  engagement: number;
  id: number;
  inbound_calls: number;
  outbound_calls: number;
  outbound_dials: number;
  person_id: number;
  updated_at: string;
  week: string;
};

// TODO: Check these (PLAYB-155)
export type Who = {
  id: number;
  name: string;
  company?: string;
  title?: string;
  tzid?: string;
  engagement_score?: number;
  sfdc_id: string;
  person_rollups?: PersonRollup[];
};

export type Person = {
  id: number;
  name: string;
};

// TODO: fix this duplicate. Type should be import from the actions rewrite.
export type TabName =
  | 'Active'
  | 'Failed'
  | 'Executed Actions'
  | 'Completed'
  | 'Outbox'
  | 'Assigned to Others';

export type OrderType = {
  field: string;
  direction: 'asc' | 'desc';
};

export type DueAtOperator = 'BEFORE' | 'ON' | 'AFTER';
export type DateTimeCondition = {
  operator: DueAtOperator;
  date_time: string;
};

// TODO: WFA-105. Adding todos on actions filter and action type
// these need to be synchronized with the omnibar action types and filters
export type ActionsFilter = {
  search?: ActionSearchInput[];
  type?: ActionType[];
  source?: SourceType[];
  due_at?: DateTimeCondition[] | DueAtOperator[];
  priority?: ActionPriorityTypes[];
  smart_priority?: ActionPriorityTypes[];
  smart_priority_cutoff?: ActionPriorityTypes[];
  exclude_people?: string[]; // TODO: identify this
  scope?: string; // TODO: identify
  status?: string; // TODO: identify
  timezone?: string[];
  company?: string[];
  person_name?: string[];
  person_title?: string[];
  related_to_name?: string[];
  flow_label?: string[];
  engagement_score?: EngagementScoreType[];
  last_touched_at?: DateTimeCondition[];
  last_engaged_at?: DateTimeCondition[];
};

type StepNameType = {
  step_id: number;
  step_name: string;
};
export type FlowNameType = {
  flow_id: number;
  flow_name: string;
  steps: StepNameType[];
};

export type FlowLabelType = {
  id: number;
  name: string;
  category: string;
};

export type RelatedToNamesType = {
  id: string;
  name: string;
  type: string;
};

export type BulkActionParams = Partial<{
  action_ids: (string | number)[];
  person_step_ids: (string | number)[];
}>;

export type BulkRescheduleActionParams = {
  execute_at: Date;
} & BulkActionParams;

/**
 * The filter object in omnibar sends an array of strings (operators)
 * the web-app endpoint expects an operator with a specific datetime
 */
export function processDueAtFilter(
  filters?: DueAtOperator[],
): DateTimeCondition[] {
  if (!filters) return [];

  const currentDate = moment().toISOString();
  return filters.map(filter => ({
    operator: filter,
    date_time: currentDate,
  }));
}

function parseTimeRange(filterString: string): DateTimeCondition {
  // e.g. "BEFORE_4_hour"
  const [operator, quantityStr, unit] = filterString?.split('_');

  const quantity = parseInt(quantityStr, 10) || 0;
  const dateTime = moment()
    .subtract(quantity, unit as unitOfTime.DurationConstructor)
    .toISOString();

  return {
    operator,
    date_time: dateTime,
  } as DateTimeCondition;
}

export function parseTimeRangeFilter(filters?: string[]): DateTimeCondition[] {
  if (!filters) return [];

  return filters
    .filter(filter => !!filter && typeof filter === 'string')
    .map(parseTimeRange);
}

export type BodyParams = {
  page: number;
  per_page: number;
  tab: TabName;
  order?: OrderType;
  filter: Partial<ActionsFilter>;
};

export type FilterBodyParams = {
  query: string;
  page: number;
  per_page: number;
  tab: TabName;
  filter: Partial<ActionsFilter>;
};

export type FetchPageFunction = (
  filter: Partial<ActionsFilter>,
  tab: TabName,
  searchTerm: string,
  page: number,
  limit: number,
) => Promise<{
  data: {
    id: string | number;
    name: string;
    type?: string;
    children?: { id: number; name: string }[];
  }[];
  meta?: { page: number; has_more?: boolean };
}>;

export const getActions = (
  bodyParams?: Partial<BodyParams>,
): Promise<GetActionsResponse> =>
  getClient().post('actions', { json: bodyParams }).json();

type ActionCounts = {
  [tabName in TabName]: number;
};

type GetActionCountsResponse = {
  data: ActionCounts;
};

export const postActionCounts = (
  filter = {} as Partial<ActionsFilter>,
): Promise<GetActionCountsResponse> =>
  getClient().post('actions/tabs_count', { json: { filter } }).json();

export type ApiTimezones = {
  data: string[];
};

export const getActionsTimezones = (): Promise<ApiTimezones> => {
  return getClient().get('actions/timezones').json();
};

export const getActionsAssignees = (): Promise<ApiAssignees> => {
  return getClient().get('actions/assignees').json();
};

export const getActionsCreators = (): Promise<ApiActionsCreators> => {
  return getClient().get('actions/creators').json();
};

export const getCompletedCompanies = (
  bodyParams?: Partial<FilterBodyParams>,
): Promise<{ data: string[]; has_more: boolean }> => {
  return getClient()
    .post('actions/companies', {
      json: bodyParams,
    })
    .json();
};

export const getActionsPersonNames = (
  bodyParams?: Partial<FilterBodyParams>,
): Promise<{ data: string[]; has_more: boolean }> => {
  return getClient()
    .post('actions/person_names', {
      json: bodyParams,
    })
    .json();
};

export const getActionsRelatedToNames = (
  bodyParams?: Partial<FilterBodyParams>,
): Promise<{ data: RelatedToNamesType[]; has_more: boolean }> => {
  return getClient()
    .post('actions/related_to_names', {
      json: bodyParams,
    })
    .json();
};

export const getActionsFlows = (
  bodyParams?: Partial<FilterBodyParams>,
): Promise<{ data: FlowNameType[]; has_more: boolean }> => {
  return getClient()
    .post('actions/flow', {
      json: bodyParams,
    })
    .json();
};

export const getActionsFlowLabels = (
  bodyParams?: Partial<FilterBodyParams>,
): Promise<{ data: FlowLabelType[]; has_more: boolean }> => {
  return getClient()
    .post('actions/flow_label', {
      json: bodyParams,
    })
    .json();
};

export const getActionsPersonTitles = (
  bodyParams?: Partial<FilterBodyParams>,
): Promise<{ data: string[]; has_more: boolean }> => {
  return getClient()
    .post('actions/person_titles', {
      json: bodyParams,
    })
    .json();
};

export const bulkRescheduleActions = (
  bodyParams?: Partial<BulkRescheduleActionParams>,
): Promise<{ data: { message: string } }> => {
  return getClient()
    .post('actions/bulk_rescheduled_actions', {
      json: bodyParams,
    })
    .json();
};

export const bulkSendNowActions = (
  bodyParams?: Partial<BulkActionParams>,
): Promise<{ data: { message: string } }> => {
  return getClient()
    .post('actions/bulk_send_now', {
      json: bodyParams,
    })
    .json();
};
